"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  PublicJobAlertSubscriptionRequestSchema,
  type PublicJobAlertSubscriptionResponseDto,
} from "@artha/api-contracts";
import {
  type JobAlertFrequency,
  type JobAlertExperience,
  type PublicJobAlertStorage,
  type PublicJobAlertFeedback,
  type PublicJobAlertSubscriptionController as SharedPublicJobAlertSubscriptionController,
  JOB_ALERT_FREQUENCIES,
  JOB_ALERT_EXPERIENCES,
} from "@artha/types";

export const DEFAULT_JOB_ALERT_FREQUENCY: JobAlertFrequency = "weekly";
export const DEFAULT_JOB_ALERT_EXPERIENCE: JobAlertExperience = "fresher";
import {
  normalizePublicJobAlertUsername,
  savePublicJobAlertSubscription,
} from "../services/jobAlertClient";

export const PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY =
  "public-job-alert-subscription-draft";

export type PublicJobAlertSubscriptionController = SharedPublicJobAlertSubscriptionController<PublicJobAlertSubscriptionResponseDto>;

const DEFAULT_STORAGE: PublicJobAlertStorage = {
  externalId: null,
  frequency: DEFAULT_JOB_ALERT_FREQUENCY,
  experience: DEFAULT_JOB_ALERT_EXPERIENCE,
};

const readStorage = (): PublicJobAlertStorage => {
  if (typeof window === "undefined") {
    return DEFAULT_STORAGE;
  }

  try {
    const raw = window.localStorage.getItem(PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY);
    if (!raw) return DEFAULT_STORAGE;

    const parsed = JSON.parse(raw) as Partial<PublicJobAlertStorage>;
    const externalId = typeof parsed.externalId === "string" ? parsed.externalId : null;
    const frequency =
      parsed.frequency && (JOB_ALERT_FREQUENCIES as readonly string[]).includes(parsed.frequency)
        ? (parsed.frequency as JobAlertFrequency)
        : DEFAULT_STORAGE.frequency;
    const experience =
      parsed.experience && (JOB_ALERT_EXPERIENCES as readonly string[]).includes(parsed.experience)
        ? (parsed.experience as JobAlertExperience)
        : DEFAULT_STORAGE.experience;

    return {
      externalId,
      frequency,
      experience,
    };
  } catch {
    return DEFAULT_STORAGE;
  }
};

export function usePublicJobAlertSubscription(
  username: string,
): PublicJobAlertSubscriptionController {
  const t = useTranslations("profile");
  const normalizedUsername = useMemo(
    () => normalizePublicJobAlertUsername(username),
    [username],
  );
  
  const [email, setEmail] = useState("");
  const [subscription, setSubscription] = useState<PublicJobAlertStorage>(readStorage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeModalCardId, setActiveModalCardId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<PublicJobAlertFeedback>({
    cardId: null,
    errorMessage: null,
    successMessage: null,
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(
        PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY,
        JSON.stringify(subscription),
      );
    } catch {
      // Ignore localStorage sync failures.
    }
  }, [subscription]);

  const setFrequency = useCallback((value: JobAlertFrequency) => {
    setSubscription((current) => ({
      ...current,
      frequency: value,
    }));
  }, []);

  const setExperience = useCallback((value: JobAlertExperience) => {
    setSubscription((current) => ({
      ...current,
      experience: value,
    }));
  }, []);

  const submit = useCallback(
    async (cardId: string, options?: { emailOnly?: boolean }) => {
      const payload: Record<string, unknown> = { email };
      if (!options?.emailOnly) {
        payload.frequency = subscription.frequency;
        payload.experience = subscription.experience;
      }

      const parsedPayload = PublicJobAlertSubscriptionRequestSchema.safeParse(payload);

      if (!parsedPayload.success) {
        setFeedback({
          cardId,
          errorMessage: t("jobAlertInvalidEmail"),
          successMessage: null,
        });
        return null;
      }

      setIsSubmitting(true);
      setFeedback({
        cardId,
        errorMessage: null,
        successMessage: null,
      });

      try {
        const response = await savePublicJobAlertSubscription(
          normalizedUsername,
          parsedPayload.data,
        );

        setSubscription((current) => ({
          externalId: response.externalId,
          frequency: response.frequency ?? current.frequency,
          experience: response.experience ?? current.experience,
        }));
        setFeedback({
          cardId,
          errorMessage: null,
          successMessage: options?.emailOnly ? null : t("jobAlertSuccess"),
        });

        return response;
      } catch (error) {
        setFeedback({
          cardId,
          errorMessage:
            error instanceof Error && error.message
              ? error.message
              : t("jobAlertError"),
          successMessage: null,
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, subscription.frequency, subscription.experience, normalizedUsername, t],
  );

  return {
    email,
    frequency: subscription.frequency,
    experience: subscription.experience,
    externalId: subscription.externalId,
    isSubmitting,
    feedback,
    setEmail,
    setFrequency,
    setExperience,
    isModalOpen: activeModalCardId !== null,
    activeModalCardId,
    setActiveModalCardId,
    submit,
  };
}
